import styled from "@emotion/styled";
import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import translate from "src/lib/translate";

/** Figma's veil over the blurred content: clear at the top edge, settling to 40% white by 18.5%
 * so the bar reads as frosted glass rather than a solid band (node 7550:65472). */
const VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 18.5%)";

/**
 * Frosted CTA bar pinned to the bottom of the hub per Figma (Medication Experience – v1, node
 * 7550:65472 "Sticky CTA (bottom)").
 *
 * Sticky rather than fixed: as a direct child of InDialog's scroll container it pins to the
 * scrollport for the whole scroll and then settles into place at the end, which also keeps it
 * inside the dialog panel on desktop instead of the browser viewport.
 *
 * 16px inset per Figma — wider than the 24px content column above it, so the button reaches
 * past the cards on purpose.
 */
const Bar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 3;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));
  background: ${VEIL_GRADIENT};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Figma's hairline is 0.3px, not a full pixel — at 1px it reads as a hard divider instead of
     the barely-there edge of a glass panel. Same weight as the MealInsights action bar. */
  border-top: 0.3px solid var(--Greys-Light-Gray, #e6e3db);

  /* InDialog's noPadding mode zeroes the last child's padding-bottom (Content in InDialog.tsx)
     and the bar is that last child, so restate it with higher specificity — otherwise the
     button sits flush against the bottom edge of the dialog. */
  &&& {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }

  /* Figma's CTA is 46px; the shared Button's default "large" is 3.0625rem. Set from here
     (higher specificity than the component's own class) rather than forking the button. */
  > button {
    --btn-height: 2.875rem;
  }
`;

/** Bottom CTA that takes the member from the refill hub into the Sophie assistant chat. */
const RefillHubAssistantCta: FC<{ onPress: () => void }> = ({ onPress }) => (
  <Bar>
    <Button theme="sunrise" grow hideArrow onPress={onPress}>
      {translate("prepareRefillDialog.v2.assistant.cta")}
    </Button>
  </Bar>
);

export default RefillHubAssistantCta;
